import {
  Delete as DeleteIcon,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import {
  AnalogueAggregation,
  AnalogueChartKey,
  AnalogueChartSettingsUpdate,
  ScenarioLayer,
  ScenarioLayerAnaloguesPayload,
  ScenarioLayerAnalogueHit,
  scenarioLayersApi,
} from '@/api/scenarioLayers';
import { scenariosApi } from '@/api/scenarios';
import { AnalogueManualCurveDialog } from './AnalogueManualCurveDialog';
import { AnalogueMapSearchDialog } from './AnalogueMapSearchDialog';
import { AnaloguePropertySearchDialog } from './AnaloguePropertySearchDialog';

const ANALOGUE_SLOT_COUNT = 10;
const PALETTE = [
  '#5c6bc0',
  '#26a69a',
  '#7e57c2',
  '#42a5f5',
  '#66bb6a',
  '#ab47bc',
  '#29b6f6',
  '#8d6e63',
  '#ec407a',
  '#78909c',
  '#9ccc65',
  '#ffa726',
  '#26c6da',
  '#bdbdbd',
  '#5d4037',
];
const AGGREGATED_COLOR = '#e65100';

interface ScenarioAnaloguesStepProps {
  scenarioId: string;
  onGoToWells?: () => void;
  readOnly?: boolean;
}

type CurveKey = 'oil' | 'water_cut_pct';

const formatProp = (value: number | null | undefined): string => {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  if (Math.abs(value) >= 1000) return value.toFixed(1);
  if (Math.abs(value) >= 1) return value.toFixed(3);
  return value.toFixed(4);
};

export const ScenarioAnaloguesStep: React.FC<ScenarioAnaloguesStepProps> = ({
  scenarioId,
  onGoToWells,
  readOnly = false,
}) => {
  const [layers, setLayers] = useState<ScenarioLayer[]>([]);
  const [payloads, setPayloads] = useState<Record<string, ScenarioLayerAnaloguesPayload>>({});
  const [scenarioCoordinate, setScenarioCoordinate] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [savingLayerId, setSavingLayerId] = useState<string | null>(null);

  const [mapDialogLayerId, setMapDialogLayerId] = useState<string | null>(null);
  const [propDialogLayerId, setPropDialogLayerId] = useState<string | null>(null);
  const [manualDialog, setManualDialog] = useState<{ layerId: string; chartKey: AnalogueChartKey } | null>(null);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [scenario, scenarioLayers] = await Promise.all([
        scenariosApi.getScenario(scenarioId),
        scenarioLayersApi.getScenarioLayers(scenarioId),
      ]);
      setScenarioCoordinate(scenario.coordinate ?? null);
      setLayers(scenarioLayers);
      const next: Record<string, ScenarioLayerAnaloguesPayload> = {};
      await Promise.all(
        scenarioLayers.map(async (layer) => {
          next[layer.scenario_layer_id] = await scenarioLayersApi.getAnalogues(layer.scenario_layer_id);
        }),
      );
      setPayloads(next);
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to load analogues');
    } finally {
      setLoading(false);
    }
  }, [scenarioId]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const saveAnalogues = async (
    layerId: string,
    analogueLayerIds: string[],
    aggregation?: AnalogueAggregation | null,
  ) => {
    setSavingLayerId(layerId);
    setError(null);
    try {
      const payload = await scenarioLayersApi.replaceAnalogues(layerId, {
        analogue_layer_ids: analogueLayerIds,
        analogue_aggregation: aggregation ?? payloads[layerId]?.analogue_aggregation ?? 'mean',
      });
      setPayloads((prev) => ({ ...prev, [layerId]: payload }));
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to save analogues');
    } finally {
      setSavingLayerId(null);
    }
  };

  const setChartSettings = async (layerId: string, body: AnalogueChartSettingsUpdate) => {
    setSavingLayerId(layerId);
    setError(null);
    try {
      const payload = await scenarioLayersApi.setAnalogueChartSettings(layerId, body);
      setPayloads((prev) => ({ ...prev, [layerId]: payload }));
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to save chart settings');
    } finally {
      setSavingLayerId(null);
    }
  };

  const ready = layers.length > 0 && layers.every((layer) => {
    const payload = payloads[layer.scenario_layer_id];
    return Boolean(payload?.analogues?.length);
  });

  const openMapSearch = (layerId: string) => {
    setMapDialogLayerId(layerId);
  };

  const openPropSearch = (layerId: string) => {
    setPropDialogLayerId(layerId);
  };

  const addAnalogueLayer = (scenarioLayerId: string, analogueLayerId: string) => {
    const current = payloads[scenarioLayerId]?.analogues || [];
    if (current.some((a) => a.layer_id === analogueLayerId)) return;
    if (current.length >= ANALOGUE_SLOT_COUNT) {
      setError(`At most ${ANALOGUE_SLOT_COUNT} analogues can be selected`);
      return;
    }
    const ids = [...current.map((a) => a.layer_id), analogueLayerId];
    saveAnalogues(scenarioLayerId, ids, payloads[scenarioLayerId]?.analogue_aggregation);
  };

  const addHit = (layerId: string, hit: ScenarioLayerAnalogueHit) => {
    addAnalogueLayer(layerId, hit.layer_id);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 1 }}>
        {readOnly ? 'Analogues' : 'Select analogues'}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {readOnly
          ? 'Historical type-wells selected for each reservoir, with the aggregated type-well curve used in the forecast. Recovery factor is shown only when analogues have an extraction rate.'
          : 'Choose historical type-wells for every reservoir. Set aggregation per chart and optionally tune oil decline, water cut, or recovery factor manually against analogue curves. Liquid rate is derived from oil and water cut for forecasting. Reservoir configuration cannot start until every reservoir has at least one analogue.'}
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {!layers.length && (
        <Alert severity="info">
          {readOnly ? 'This scenario has no reservoirs.' : 'Add reservoirs first, then return here to pick analogues.'}
        </Alert>
      )}
      {layers.map((layer) => {
        const payload = payloads[layer.scenario_layer_id];
        if (!payload) return null;
        return (
          <LayerAnaloguesCard
            key={layer.scenario_layer_id}
            payload={payload}
            saving={savingLayerId === layer.scenario_layer_id}
            readOnly={readOnly}
            onRemove={(analogueLayerId) => {
              const ids = payload.analogues
                .filter((a) => a.layer_id !== analogueLayerId)
                .map((a) => a.layer_id);
              saveAnalogues(layer.scenario_layer_id, ids, payload.analogue_aggregation);
            }}
            onChartSettings={(body) => setChartSettings(layer.scenario_layer_id, body)}
            onOpenManual={(chartKey) => setManualDialog({ layerId: layer.scenario_layer_id, chartKey })}
            onAddByName={() => openMapSearch(layer.scenario_layer_id)}
            onAddByProperties={() => openPropSearch(layer.scenario_layer_id)}
            slotsFull={(payload.analogues?.length || 0) >= ANALOGUE_SLOT_COUNT}
          />
        );
      })}
      {!readOnly && layers.length > 0 && (
        <Box sx={{ mt: 3 }}>
          {!ready && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Every reservoir needs at least one analogue before Reservoir configuration.
            </Alert>
          )}
          <Button variant="contained" disabled={!ready} onClick={onGoToWells}>
            Save and go to Reservoir configuration
          </Button>
        </Box>
      )}

      {!readOnly && (
        <>
      <AnalogueMapSearchDialog
        open={Boolean(mapDialogLayerId)}
        selectedLayerIds={
          mapDialogLayerId
            ? (payloads[mapDialogLayerId]?.analogues || []).map((a) => a.layer_id)
            : []
        }
        adding={Boolean(mapDialogLayerId && savingLayerId === mapDialogLayerId)}
        scenarioCoordinate={scenarioCoordinate}
        onClose={() => setMapDialogLayerId(null)}
        onAdd={(analogueLayerId) => {
          if (mapDialogLayerId) addAnalogueLayer(mapDialogLayerId, analogueLayerId);
        }}
      />

      <AnaloguePropertySearchDialog
        open={Boolean(propDialogLayerId)}
        layer={layers.find((item) => item.scenario_layer_id === propDialogLayerId) ?? null}
        excludeLayerIds={
          propDialogLayerId
            ? (payloads[propDialogLayerId]?.analogues || []).map((a) => a.layer_id)
            : []
        }
        adding={Boolean(propDialogLayerId && savingLayerId === propDialogLayerId)}
        onClose={() => setPropDialogLayerId(null)}
        onAdd={(hit) => {
          if (propDialogLayerId) addHit(propDialogLayerId, hit);
        }}
      />
      <AnalogueManualCurveDialog
        open={Boolean(manualDialog)}
        chartKey={manualDialog?.chartKey ?? null}
        analogues={
          manualDialog
            ? (payloads[manualDialog.layerId]?.analogues || [])
            : []
        }
        manual={manualDialog ? payloads[manualDialog.layerId]?.manual : undefined}
        saving={Boolean(manualDialog && savingLayerId === manualDialog.layerId)}
        onClose={() => setManualDialog(null)}
        onSave={(chartKey, manualSlice) => {
          if (!manualDialog) return;
          setChartSettings(manualDialog.layerId, { manual: { [chartKey]: manualSlice } }).then(() => {
            setManualDialog(null);
          });
        }}
      />
        </>
      )}
    </Box>
  );
};

function aggregationLabel(method: AnalogueAggregation | null | undefined): string {
  if (method === 'mean') return 'Mean';
  if (method === 'weighted_mean') return 'Weighted mean';
  if (method === 'manual') return 'Manual';
  if (method === 'p10') return 'P10';
  if (method === 'p50') return 'P50';
  if (method === 'p90') return 'P90';
  return '—';
}

function chartAggregations(payload: ScenarioLayerAnaloguesPayload): AnalogueChartAggregations {
  const fallback = payload.analogue_aggregation ?? 'mean';
  const base = payload.aggregations ?? payload.aggregated?.methods ?? {
    oil: fallback,
    liquid: fallback,
    water_cut_pct: fallback,
    recovery_factor: fallback,
  };
  return {
    oil: payload.manual?.oil?.enabled ? 'manual' : base.oil,
    liquid: base.liquid,
    water_cut_pct: payload.manual?.water_cut_pct?.enabled ? 'manual' : base.water_cut_pct,
    recovery_factor: payload.manual?.recovery_factor?.enabled ? 'manual' : base.recovery_factor,
  };
}

function aggregatedLineLabel(
  chartKey: AnalogueChartKey,
  payload: ScenarioLayerAnaloguesPayload,
): string {
  const method = chartAggregations(payload)[chartKey];
  return `Aggregated (${aggregationLabel(method)})`;
}

function LayerAnaloguesCard({
  payload,
  saving,
  readOnly = false,
  onRemove,
  onChartSettings,
  onOpenManual,
  onAddByName,
  onAddByProperties,
  slotsFull,
}: {
  payload: ScenarioLayerAnaloguesPayload;
  saving: boolean;
  readOnly?: boolean;
  onRemove: (analogueLayerId: string) => void;
  onChartSettings: (body: AnalogueChartSettingsUpdate) => void;
  onOpenManual: (chartKey: AnalogueChartKey) => void;
  onAddByName: () => void;
  onAddByProperties: () => void;
  slotsFull: boolean;
}) {
  const aggregations = chartAggregations(payload);
  const columns = readOnly
    ? payload.analogues
    : Array.from({ length: ANALOGUE_SLOT_COUNT }, (_, idx) => payload.analogues[idx] ?? null);
  const filled = columns.filter((col): col is ScenarioLayerAnalogueHit => col != null);

  const buildLineData = (key: CurveKey) => {
    const n = Math.max(
      payload.aggregated?.[key]?.length || 0,
      ...filled.map((c) => c.curves?.[key]?.length || 0),
      1,
    );
    return Array.from({ length: n }, (_, year) => {
      const point: Record<string, number | null> = { year };
      filled.forEach((col, idx) => {
        const series = col.curves?.[key] || [];
        point[`a${idx}`] = year < series.length ? series[year] : null;
      });
      const agg = payload.aggregated?.[key];
      point.agg = agg && year < agg.length ? agg[year] : null;
      return point;
    });
  };

  const oilData = useMemo(() => buildLineData('oil'), [filled, payload.aggregated]);
  const wcData = useMemo(() => buildLineData('water_cut_pct'), [filled, payload.aggregated]);
  const recoveryData = useMemo(() => {
    const rows = filled.map((col, idx) => ({
      id: col.layer_id,
      name: `${col.field_name} / ${col.layer_name}`,
      recovery_factor: col.recovery_factor,
      fill: PALETTE[idx % PALETTE.length],
    }));
    if (payload.aggregated) {
      rows.push({
        id: 'agg',
        name: aggregatedLineLabel('recovery_factor', payload),
        recovery_factor: payload.aggregated.recovery_factor ?? payload.recovery_factor,
        fill: AGGREGATED_COLOR,
      });
    }
    return rows;
  }, [filled, payload]);

  const renderAggregationSelect = (chartKey: AnalogueChartKey) => {
    const method = aggregations[chartKey];
    return (
    <FormControl size="small" sx={{ minWidth: 150 }}>
      <InputLabel>Aggregation</InputLabel>
      <Select
        label="Aggregation"
        value={method}
        onChange={(e) => onChartSettings({
          aggregations: { [chartKey]: e.target.value as AnalogueAggregation },
        })}
        disabled={saving || !filled.length}
      >
        {method === 'manual' && (
          <MenuItem value="manual">Manual</MenuItem>
        )}
        <MenuItem value="mean">Mean</MenuItem>
        <MenuItem value="weighted_mean">Weighted mean</MenuItem>
        <MenuItem value="p10">P10</MenuItem>
        <MenuItem value="p50">P50</MenuItem>
        <MenuItem value="p90">P90</MenuItem>
      </Select>
    </FormControl>
    );
  };

  const renderLineChart = (
    title: string,
    chartKey: Exclude<AnalogueChartKey, 'recovery_factor'>,
    chartData: Array<Record<string, number | null>>,
    yLabel: string,
  ) => (
    <Paper variant="outlined" sx={{ p: 1.5, height: 360 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
        {!readOnly && (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            {renderAggregationSelect(chartKey)}
            <Button
              size="small"
              variant="outlined"
              onClick={() => onOpenManual(chartKey)}
              disabled={saving || !filled.length}
            >
              {payload.manual?.[chartKey]?.enabled ? 'Edit manual' : 'Set manually'}
            </Button>
          </Box>
        )}
        {readOnly && (
          <Typography variant="caption" color="text.secondary">
            {aggregationLabel(aggregations[chartKey])}
          </Typography>
        )}
      </Box>
      <ResponsiveContainer width="100%" height="82%">
        <LineChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="year" tick={{ fontSize: 11 }} />
          <YAxis
            tick={{ fontSize: 11 }}
            width={48}
            label={{
              value: yLabel,
              angle: -90,
              position: 'insideLeft',
              offset: 0,
              style: { fontSize: 11, textAnchor: 'middle' },
            }}
          />
          <Tooltip />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          {filled.map((col, idx) => (
            <Line
              key={col.layer_id}
              type="monotone"
              dataKey={`a${idx}`}
              name={`${col.field_name} / ${col.layer_name}`}
              stroke={PALETTE[idx % PALETTE.length]}
              dot={false}
              strokeWidth={1.5}
              isAnimationActive={false}
            />
          ))}
          {payload.aggregated && (
            <Line
              type="monotone"
              dataKey="agg"
              name={aggregatedLineLabel(chartKey, payload)}
              stroke={AGGREGATED_COLOR}
              dot={false}
              strokeWidth={2.5}
              isAnimationActive={false}
            />
          )}
        </LineChart>
      </ResponsiveContainer>
    </Paper>
  );

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="h6">{payload.layer_name}</Typography>
          {!readOnly && (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button size="small" variant="outlined" onClick={onAddByName} disabled={saving || slotsFull}>Add by name</Button>
            <Button size="small" variant="outlined" onClick={onAddByProperties} disabled={saving || slotsFull}>Add by properties</Button>
          </Box>
          )}
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Recovery factor: {payload.recovery_factor == null ? '—' : payload.recovery_factor.toFixed(3)}
          {!readOnly && saving ? '  Saving…' : ''}
          {!readOnly && slotsFull ? `  ${ANALOGUE_SLOT_COUNT} analogues selected.` : ''}
        </Typography>
        {readOnly && !filled.length && (
          <Alert severity="info" sx={{ mb: 2 }}>No analogues selected for this reservoir.</Alert>
        )}
        <TableContainer sx={{ mb: 2, overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: readOnly ? 480 : 1100, tableLayout: 'fixed' }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 160 }}>Property</TableCell>
                <TableCell sx={{ width: 140 }}>{payload.layer_name}</TableCell>
                {columns.map((col, idx) => (
                  <TableCell key={col?.layer_id ?? `slot-${idx}`} sx={{ width: 140 }}>
                    {col ? (
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                        <span>{col.field_name} / {col.layer_name}</span>
                        {!readOnly && (
                          <IconButton size="small" onClick={() => onRemove(col.layer_id)} sx={{ mt: -0.5 }}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.disabled">
                        Analogue {idx + 1}
                      </Typography>
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {(payload.attribute_rows || []).map((row) => (
                <TableRow key={row.id}>
                  <TableCell>{row.name}{row.unit ? ` (${row.unit})` : ''}</TableCell>
                  <TableCell>{formatProp(payload.target_properties?.[row.id])}</TableCell>
                  {columns.map((col, idx) => (
                    <TableCell key={col?.layer_id ?? `slot-${idx}-${row.id}`}>
                      {col ? formatProp(col.properties?.[row.id]) : '—'}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
              <TableRow>
                <TableCell>Recovery factor</TableCell>
                <TableCell>{payload.recovery_factor == null ? '—' : payload.recovery_factor.toFixed(3)}</TableCell>
                {columns.map((col, idx) => (
                  <TableCell key={col?.layer_id ?? `slot-kin-${idx}`}>
                    {col
                      ? (col.recovery_factor == null ? '—' : col.recovery_factor.toFixed(3))
                      : '—'}
                  </TableCell>
                ))}
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 2, mb: 2 }}>
          {renderLineChart('Oil decline', 'oil', oilData, 'thous. t')}
          {renderLineChart('Water cut', 'water_cut_pct', wcData, '%')}
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2 }}>
          <Paper variant="outlined" sx={{ p: 1.5, height: 360 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Recovery factor
              </Typography>
              {!readOnly && (
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                  {renderAggregationSelect('recovery_factor')}
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => onOpenManual('recovery_factor')}
                    disabled={saving || !filled.length}
                  >
                    {payload.manual?.recovery_factor?.enabled ? 'Edit manual' : 'Set manually'}
                  </Button>
                </Box>
              )}
              {readOnly && (
                <Typography variant="caption" color="text.secondary">
                  {aggregationLabel(aggregations.recovery_factor)}
                </Typography>
              )}
            </Box>
            <ResponsiveContainer width="100%" height="82%">
              <BarChart data={recoveryData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" hide />
                <YAxis tick={{ fontSize: 11 }} domain={[0, 'auto']} />
                <Tooltip />
                <Bar dataKey="recovery_factor" name="Recovery factor" isAnimationActive={false}>
                  {recoveryData.map((entry) => (
                    <Cell key={entry.id} fill={entry.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Box>
      </CardContent>
    </Card>
  );
}
